<script setup lang="ts">
const site = useSiteStore()
const portfolioStore = usePortfolioStore()
const pageReady = useState('portfolio-page-ready', () => false)
const minimumLoaderTime = 450
const loaderStartedAt = Date.now()
let readyTimer: ReturnType<typeof setTimeout> | undefined

type PortfolioMeta = {
  profile?: import('~/types/portfolio').Profile
  menu?: import('~/types/portfolio').MenuItem[]
  template?: string
  templateId?: string
  template_id?: string
  selectedTemplate?: string
  selected_template?: string
  theme?: string
}

type PortfolioResponse = PortfolioMeta | { data?: PortfolioMeta }

function resolveTemplateId(value: string | undefined, activePackage?: string | number) {

  if (!value || !activePackage) return 'neon-temp'
  if(activePackage == 1 || activePackage == 2) return 'neon-temp'

  const normalized = value.toLowerCase().trim()
  if (normalized.includes('aura')) return 'aura-temp'
  if (normalized.includes('atlas')) return 'atlas-temp'
  if (normalized.includes('neon')) return 'neon-temp'

  return site.templates.some(template => template.id === value) ? value : undefined
}

/**
 * فیلدهای رسانه‌ای پروفایل را به رشته‌ی مسیر تبدیل می‌کند.
 * API برای `avatar`/`portrait` گاهی آبجکت رسانه می‌فرستد و همین باعث
 * ارور `profile.avatar.startsWith is not a function` و `src="[object Object]"`
 * در قالب‌ها می‌شد.
 */
function normalizeProfileMedia<T extends Record<string, unknown>>(meta: T): T {
  const mediaKeys = ['avatar', 'portrait', 'cover', 'banner', 'logo', 'image']
  const normalized: Record<string, unknown> = { ...meta }

  for (const key of mediaKeys) {
    if (!(key in normalized)) continue
    normalized[key] = resolveMediaUrl(normalized[key]) ?? ''
  }

  // بعضی پاسخ‌ها پروفایل را یک سطح تودرتو می‌فرستند.
  const nested = normalized.profile
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    normalized.profile = normalizeProfileMedia(nested as Record<string, unknown>)
  }

  return normalized as T
}

// این پاسخ تنظیمات زنده‌ی کاربر است (قالب انتخابی، منو، پروفایل) و از داشبورد
// تغییر می‌کند، پس عمداً کش نمی‌شود — نه این‌جا و نه در پروکسی سمت سرور. نسخه‌ی
// قبلی با `storage: 'local'` و TTL ده‌دقیقه‌ای باعث می‌شد بعد از انتخاب قالب در
// داشبورد، مرورگر تا دقایقی قالب قدیمی را نگه دارد.
const portfolioRequest = useApi<PortfolioResponse>('/v1/general/portfolio', 'portfolio', {
  timeout: 5000,
  server: true,
  lazy: false
})

if (import.meta.server) {
  await portfolioRequest
}

const {
  data: portfolio,
  error: portfolioError,
  pending: portfolioPending,
  status: portfolioStatus,
  refresh: refreshPortfolio
} = portfolioRequest

function applyPortfolio(response: PortfolioResponse) {
  const portfolioMeta = 'data' in response ? response.data : response
  if (!portfolioMeta) return
  console.log(portfolioMeta);
  const activePackage = portfolioMeta.active_package?.type || ''
  const selectedTemplate = portfolioMeta.template
    ?? portfolioMeta.templateId
    ?? portfolioMeta.template_id
    ?? portfolioMeta.selectedTemplate
    ?? portfolioMeta.selected_template
  const selectedTemplateId = resolveTemplateId(selectedTemplate, activePackage)

  if (selectedTemplateId) site.setTemplate(selectedTemplateId)
  portfolioStore.profile = normalizeProfileMedia(
    portfolioMeta as unknown as Record<string, unknown>
  )
  portfolioStore.setMenu(portfolioMeta.menu)

  if (import.meta.server) {
    pageReady.value = true
    return
  }

  const elapsed = Date.now() - loaderStartedAt
  const remaining = Math.max(0, minimumLoaderTime - elapsed)
  clearTimeout(readyTimer)
  readyTimer = setTimeout(() => {
    pageReady.value = true
  }, remaining)
}

watch(
  portfolio,
  value => {
    if (value) applyPortfolio(value)
  },
  { immediate: true }
)

const portfolioExpired = computed(() => {
  const error = portfolioError.value as {
    status?: number
    statusCode?: number
  } | null

  return error?.statusCode === 403 || error?.status === 403
})

const portfolioFailed = computed(() => {
  if (portfolioError.value || portfolioStatus.value === 'error') return true
  if (portfolioStatus.value !== 'success') return false

  const response = portfolio.value
  return !response || ('data' in response && !response.data)
})

// اگر در SSR داده‌ای نرسید، HTML خروجی فقط لودر است. خطای پایان اشتراک
// همان 403 می‌ماند تا مرورگر و ربات‌ها معنای درست پاسخ را دریافت کنند؛ خطاهای
// موقت با 503 به گوگل می‌گویند بعداً دوباره سر بزند.
if (import.meta.server && (portfolioFailed.value || !pageReady.value)) {
  setResponseStatus(useRequestEvent()!, portfolioExpired.value ? 403 : 503)
}

useSeoMeta({
  title: () => portfolioExpired.value
    ? 'این سایت موقتاً در دسترس نیست'
    : undefined,
  robots: () => portfolioExpired.value ? 'noindex, nofollow' : undefined
})

const retryPortfolio = async () => {
  pageReady.value = false
  await refreshPortfolio()
}

onBeforeUnmount(() => clearTimeout(readyTimer))

// The template comes from the portfolio API. Only restore the user's visual theme.
onMounted(() => {
  const savedTheme = localStorage.getItem('portify:theme') as typeof site.theme | null
  if (savedTheme && site.current.themes.includes(savedTheme)) {
    site.setTheme(savedTheme)
  }
})

useHead({ htmlAttrs: { 'data-theme': computed(() => site.theme) } })
</script>

<template>
  <Transition name="portfolio-ready" mode="out-in">
    <SubscriptionExpired
      v-if="portfolioExpired"
      key="subscription-expired"
      :retrying="portfolioPending"
      @retry="retryPortfolio"
    />

    <AppLoader
      v-else-if="!pageReady"
      key="loader"
      :failed="portfolioFailed"
      :retrying="portfolioPending"
      @retry="retryPortfolio"
    />

    <div v-else key="portfolio" class="min-h-dvh">
      <NuxtPage />
    </div>
  </Transition>
</template>

<style>
.portfolio-ready-enter-active,
.portfolio-ready-leave-active {
  transition: opacity 0.32s ease, transform 0.32s ease;
}

.portfolio-ready-enter-from {
  opacity: 0;
  transform: translateY(8px);
}

.portfolio-ready-leave-to {
  opacity: 0;
  transform: scale(0.985);
}
</style>
